import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent } from "@angular/common/http";
import { inject } from "@angular/core";
import { BehaviorSubject, Observable, catchError, filter, switchMap, take, throwError } from "rxjs";
import { apis } from "src/app/config/api";
import { AuthService } from "src/app/services/auth.service";
import { NotificationService } from "src/app/services/notification.service";

let refreshInProgress = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

const excludedUrls = [
  apis.login,
  apis.register,
  apis.resetPassword,
  apis.forgot,
  apis.refreshToken
];

export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {

  const notification = inject(NotificationService);
  const authService = inject(AuthService);

  return next(req).pipe(
    catchError((err) => {
      if ([401, 403].includes(err.status) && !excludedUrls.some(link => req.url.includes(link))) {
        return handle401Error(req, next, authService);
      }

      if ([400, 409].includes(err.status)) {
        notification.warning(err?.error?.message || 'Something went wrong!');
      } else {
        notification.error(err?.error?.message || 'Something went wrong!');
      }

      return throwError(() => err);
    })
  );
};

function handle401Error(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService
): Observable<HttpEvent<any>> {
  if (!authService.getRefreshToken()) {
    authService.logout();
    return throwError(() => new Error('No refresh token available'));
  }

  if (!refreshInProgress) {
    refreshInProgress = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap((tokens: any) => {
        refreshInProgress = false;

        authService.saveTokens(tokens);
        refreshTokenSubject.next(tokens.access.token);

        const retryReq = req.clone({
          setHeaders: { Authorization: `Bearer ${tokens.access.token}` }
        });

        return next(retryReq);
      }),
      catchError(err => {
        refreshInProgress = false;

        // Logout ONLY if refresh token is invalid
        if (err.status === 401 || err.status === 403) {
          authService.logout();
        }

        // Otherwise, just propagate error (network/server issue)
        return throwError(() => err);
      })
    );

  } else {
    return refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => {
        const retryReq = req.clone({
          setHeaders: { Authorization: `Bearer ${token}` }
        });
        return next(retryReq);
      })
    );
  }
}
